import {
  History,
  InfoOutlined,
  NoteAlt,
  Paid,
  Task,
  Today,
} from '@mui/icons-material';
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import {
  Avatar,
  Box,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  useColorScheme,
} from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import { blueGrey } from '@mui/material/colors';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { EventService } from '@pingum/api-services';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import User from '../../../../../../../components/User';
import { setGlobalModals } from '../../../../../../../redux/app/actions';
import {
  SCHEDULE_MODAL_TYPE,
  TASK_MODAL_TYPE,
} from '../../../../../../../redux/app/types';
import { DEFAULT_NOTE_TYPE_FILTERS } from '../../../../../../../redux/noteTypeFilters/actions';
import BillableService from '../../../../../../../services/BillableService';
import HorseService from '../../../../../../../services/HorseService';
import ScheduleExceptionService from '../../../../../../../services/ScheduleExceptionService';
import ScheduleService from '../../../../../../../services/ScheduleService';
import { grey } from '../../../../../../../utils/brandColors';
import {
  generateEventsFromSchedules,
  getBarnUsersForAssignmentString,
} from '../../../../../../../utils/scheduleHelper';

const formatCurrency = (value) => {
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return value || '';
  }
  return `$${numericValue.toFixed(2)}`;
};

const buildTimelineEntry = ({
  type,
  title,
  description,
  date,
  id,
  rangeStart,
  rangeEnd,
  data = null,
  onPress = () => { },
  condensed,
}) => {
  if (!date) {
    return null;
  }
  const timestampValue = new Date(date).getTime();
  if (Number.isNaN(timestampValue)) {
    return null;
  }
  if (rangeStart && timestampValue < rangeStart) {
    return null;
  }
  if (rangeEnd && timestampValue > rangeEnd) {
    return null;
  }
  return {
    id: `${type}-${id || timestampValue}`,
    type,
    title: title || type,
    description: description || '',
    timestamp: `${moment(timestampValue).format(condensed
      ? 'll'
      : 'dddd MMMM Do')}`,
    relativeTime: moment(timestampValue).fromNow(),
    sortDate: timestampValue,
    data,
    onPress,
  };
};

const iconMap = {
  note: <NoteAlt fontSize="small" sx={{ color: '#fff' }} />,
  task: <Task fontSize="small" sx={{ color: '#fff' }} />,
  billing: <Paid fontSize="small" sx={{ color: '#fff' }} />,
  event: <Today fontSize="small" sx={{ color: '#fff' }} />,
};

const iconMap2 = {
  note: <NoteAlt fontSize="small" />,
  task: <Task fontSize="small" />,
  billing: <Paid fontSize="small" />,
  'calendar event': <Today fontSize="small" />,
};

const getTimelineColor = (type) => {
  switch (type) {
    case 'note':
      return 'secondary';
    case 'billing':
      return 'success';
    default:
      return 'primary';
  }
};

const ActivityCard = ({
  label,
  title,
  description,
  details,
  onViewDetails,
  colorMode,
  users,
  condensed,
}) => (
  <Box
    role="button"
    tabIndex={0}
    onClick={onViewDetails}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        onViewDetails();
      }
    }}
    sx={() => ({
      backgroundColor: colorMode === 'light'
        ? blueGrey[50]
        : blueGrey[900],
      borderRadius: 2,
      p: 1.5,
      cursor: onViewDetails
        ? 'pointer'
        : 'default',
      transition: 'transform 150ms ease, box-shadow 150ms ease',
      '&:hover': onViewDetails
        ? {
          transform: 'translateY(-2px)',
          boxShadow: 3,
        }
        : {},
    })}

  >
    <Stack
      spacing={1}
      direction={condensed
        ? 'column'
        : 'row'}
      justifyContent="space-between"
    >
      <Stack spacing={0.25} sx={{ textAlign: 'left' }}>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: '2',
            WebkitBoxOrient: 'vertical',
          }}
        >
          {title}
        </Typography>
        {label && (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {iconMap2[label.toLowerCase()] || <InfoOutlined fontSize="small" />}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              {label}
            </Typography>
          </Stack>
        )}
      </Stack>
      {description && (
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      )}
      {details && details.length > 0 && (
        <Stack spacing={0.25}>
          {details.map((detail, index) => (
            <Typography key={`${detail || 'detail'}-${index.toString()}`} variant="caption" color="text.secondary">
              {detail}
            </Typography>
          ))}
          {
            users && users.length > 0 && (
              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">{users.map((user) => (<User key={user.id} chip user={user} disableGutters />))}
              </Stack>
            )
          }
        </Stack>
      )}
    </Stack>
  </Box>
);

const NoteActivity = ({
  item,
  onView,
  colorMode,
  condensed,
}) => {
  const note = item.data || {};
  const author = note.createdBy?.name || note.user?.name || note.createdByName;
  const details = [
    note.type && `Type: ${note.type}`,
    author && `Author: ${author}`,
  ].filter(Boolean);
  return (
    <ActivityCard
      label="Note"
      title={item.title}
      description={item.description}
      details={details}
      onViewDetails={() => onView(item, 'note')}
      colorMode={colorMode}
      condensed={condensed}
    />
  );
};

const TaskActivity = ({
  item,
  onView,
  colorMode,
  barnUsersMap = {},
  condensed,
}) => {
  const task = item.data || {};
  const details = [
    task.completeDate
      ? 'Status: Complete'
      : 'Status: Incomplete',
  ].filter(Boolean);
  return (
    <ActivityCard
      label="Task"
      title={item.title}
      description={item.description}
      details={details}
      onViewDetails={() => onView(item, 'task')}
      colorMode={colorMode}
      users={getBarnUsersForAssignmentString(item?.data?.currentEventStatus?.assignment || '', barnUsersMap)}
      condensed={condensed}
    />
  );
};

const EventActivity = ({
  item,
  onView,
  colorMode,
  condensed,
}) => {
  const schedule = item.data || {};
  const startTime = schedule.startDate
    ? moment(schedule.startDate).format('MMM D, h:mm A')
    : null;
  const endTime = schedule.endDate
    ? moment(schedule.endDate).format('h:mm A')
    : null;
  const details = [
    schedule.location?.name && `Location: ${schedule.location.name}`,
    startTime && (endTime
      ? `Time: ${startTime} – ${endTime}`
      : `Time: ${startTime}`),
    schedule.eventTemplate?.name && `Template: ${schedule.eventTemplate.name}`,
    item.relativeTime && `Updated ${item.relativeTime}`,
  ].filter(Boolean);
  return (
    <ActivityCard
      label="Calendar Event"
      title={item.title}
      description={item.description}
      details={details}
      onViewDetails={() => onView(item, 'event')}
      colorMode={colorMode}
      users={item?.data?.participants || []}
      condensed={condensed}
    />
  );
};

const BillingActivity = ({
  item,
  onView,
  colorMode,
  condensed,
}) => {
  const billable = item.data || {};
  const amount = billable.price
    ? formatCurrency(billable.price)
    : null;
  const details = [
    amount && `Amount: ${amount}`,
    billable.status && `Status: ${billable.status}`,
    billable.taskType?.name && `Task Type: ${billable.taskType.name}`,
  ].filter(Boolean);
  return (
    <ActivityCard
      label="Billing"
      title={item.title}
      details={details}
      onViewDetails={() => onView(item, 'billing')}
      colorMode={colorMode}
      condensed={condensed}
    />
  );
};

const DefaultActivity = ({
  item,
  onView,
  colorMode,
}) => (
  <ActivityCard
    label="Activity"
    title={item.title}
    description={item.description}
    details={[item.relativeTime && `Updated ${item.relativeTime}`].filter(Boolean)}
    onViewDetails={() => onView(item, 'activity')}
    colorMode={colorMode}
  />
);

const onViewNote = (note) => {
  note.onPress(note.data)
  // TODO: implement note view modal
};

const onViewTask = (task) => {
  task.onPress(task.data)
  // TODO: implement task view modal
};

const onViewEvent = (event) => {
  event.onPress(event.data)
  // TODO: implement calendar event view modal
};

const onViewBillable = (billable) => {
  if (!billable) {
    return;
  }
  const {
    onPress,
    data,
  } = billable;
  if (typeof onPress === 'function') {
    onPress(data);
  }
};

const onViewActivity = () => {
  // TODO: implement generic activity modal
};

const ActivityComponentMap = {
  note: NoteActivity,
  task: TaskActivity,
  billing: BillingActivity,
  event: EventActivity,
  activity: DefaultActivity,
};

const ActivityViewHandlers = {
  note: onViewNote,
  task: onViewTask,
  billing: onViewBillable,
  event: onViewEvent,
  activity: onViewActivity,
};

ActivityCard.propTypes = {
  label: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  details: PropTypes.arrayOf(PropTypes.string),
  onViewDetails: PropTypes.func,
  colorMode: PropTypes.string,
};

ActivityCard.defaultProps = {
  label: '',
  description: '',
  details: [],
  onViewDetails: null,
  colorMode: 'light',
};

NoteActivity.propTypes = {
  item: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    data: PropTypes.object,
    relativeTime: PropTypes.string,
  }).isRequired,
  onView: PropTypes.func.isRequired,
};

TaskActivity.propTypes = {
  item: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    data: PropTypes.object,
    relativeTime: PropTypes.string,
  }).isRequired,
  onView: PropTypes.func.isRequired,
};

EventActivity.propTypes = {
  item: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    data: PropTypes.object,
    relativeTime: PropTypes.string,
  }).isRequired,
  onView: PropTypes.func.isRequired,
};

BillingActivity.propTypes = {
  item: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    data: PropTypes.object,
    relativeTime: PropTypes.string,
  }).isRequired,
  onView: PropTypes.func.isRequired,
};

DefaultActivity.propTypes = {
  item: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    relativeTime: PropTypes.string,
  }).isRequired,
  onView: PropTypes.func.isRequired,
};

const HorseActivityTimeline = ({
  horseId,
  openAddEditNoteModal,
  openAddEditBillableModal,
  condensed,
}) => {
  const dispatch = useDispatch();

  const selectedBarn = useSelector((state) => state.barn.selectedBarn);
  const barnUsersMap = useSelector((state) => state.barn.barnUsersMap);
  const selectedLocation = useSelector((state) => state.location.selectedLocation);
  const currentHorseLodging = useSelector((state) => state.horse.currentHorseLodging);
  const horseTagId = currentHorseLodging?.pingumTagId
    ? parseInt(currentHorseLodging.pingumTagId, 10)
    : null;
  const normalizedHorseId = horseId && Number.isNaN(Number(horseId))
    ? horseId
    : Number(horseId);
  const [activity, setActivity] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [dateRange, setDateRange] = React.useState(() => ([
    moment().subtract(30, 'days').startOf('day'),
    moment(),
  ]));
  const [quickRange, setQuickRange] = React.useState('last30');
  const [selectedTypes, setSelectedTypes] = React.useState([]);

  const shortcutItems = React.useMemo(() => ([
    {
      label: 'Last 7 Days',
      value: 'last7',
      getValue: () => [moment().subtract(7, 'days').startOf('day'), moment()],
    },
    {
      label: 'Last 30 Days',
      value: 'last30',
      getValue: () => [moment().subtract(30, 'days').startOf('day'), moment()],
    },
    {
      label: 'This Month',
      value: 'thisMonth',
      getValue: () => [moment().startOf('month'), moment().endOf('day')],
    },
    {
      label: 'Last Month',
      value: 'lastMonth',
      getValue: () => {
        const prev = moment().subtract(1, 'month');
        return [prev.clone().startOf('month'), prev.clone().endOf('month')];
      },
    },
  ]), []);

  const selectTaskOrEvent = (item, type = 'task') => {
    if (type === 'task') {
      dispatch(setGlobalModals(TASK_MODAL_TYPE, item, null, null, {}, () => { }));
    } else {
      dispatch(setGlobalModals(SCHEDULE_MODAL_TYPE, null, item.resource, item, {
        locationId: selectedLocation.id, // if set, this is a calendar event
        taskTagIds: [],
        eventTemplateTagTypeFilter: -1,
        openInSeries: false,
        startDate: null,
      }, (reload) => {
        if (reload) {
          // TODO callback to board to update?
        }
      }));
    }
  }

  const loadActivities = React.useCallback(async () => {
    if (!horseId) {
      setActivity([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const [selectedStart, selectedEnd] = dateRange || [];
    const rangeStart = selectedStart
      ? selectedStart.clone().startOf('day')
      : moment().subtract(1, 'month').startOf('day');
    const rangeEnd = selectedEnd
      ? selectedEnd.clone().endOf('day')
      : moment();
    const rangeStartValue = rangeStart.valueOf();
    const rangeEndValue = rangeEnd.valueOf();

    try {
      const notesPromise = HorseService.getHorseNotes(
        horseId,
        0,
        100,
        ['&sort=createdTimestamp,desc'],
        null,
        DEFAULT_NOTE_TYPE_FILTERS,
      ).catch(() => ({ content: [] }));

      const tasksPromise = horseTagId
        ? EventService.searchEvents({
          startDate: rangeStart.clone(),
          endDate: rangeEnd.clone(),
          includeArchived: true,
          includeComplete: true,
          searchText: '',
          statusIds: [],
          tagConditions: [[horseTagId]],
          presetDateFilter: null,
        }, 0, 100, true).catch(() => ({ content: [] }))
        : Promise.resolve({ content: [] });

      const schedulesPromise = horseTagId
        ? ScheduleService.searchSchedules(
          {
            startDate: rangeStart.toDate(),
            endDate: rangeEnd.toDate(),
            tagConditions: [[horseTagId], [selectedBarn.barnMetadata.eventTaskTypeTagId]],
            includeScheduleExceptions: false,
            includeChildren: false,
          },
          0,
          100,
          '&sort=startDate,desc',
        ).catch(() => ({ content: [] }))
        : Promise.resolve({ content: [] });
      const scheduleExceptionsPromise = horseTagId
        ? ScheduleExceptionService.getScheduleExceptions(
          rangeStart.toDate(),
          rangeEnd.toDate(),
          [horseTagId],
          0,
          100,
          '&sort=exceptionDate,desc',
        ).catch(() => ({ content: [] }))
        : Promise.resolve({ content: [] });

      const billablesPromise = normalizedHorseId
        ? BillableService.getBillables(
          [normalizedHorseId],
          [],
          '',
          '',
          '',
          '',
          rangeStart.toDate(),
          rangeEnd.toDate(),
          0,
          100,
          '&sort=billableDate,desc',
        ).then((response) => response?.data || response || { content: [] })
          .catch(() => ({ content: [] }))
        : Promise.resolve({ content: [] });

      const [
        notesResponse,
        tasksResponse,
        schedulesResponse,
        billablesResponse,
        scheduleExceptionsResponse,
      ] = await Promise.all([
        notesPromise,
        tasksPromise,
        schedulesPromise,
        billablesPromise,
        scheduleExceptionsPromise,
      ]);

      const noteEntries = (notesResponse?.content || []).map((note) => buildTimelineEntry({
        type: 'note',
        title: note.title || note.type || 'Horse Note',
        description: note.note
          || note.description
          || note.information?.description
          || '',
        date: note.createdTimestamp,
        id: note.id,
        rangeStart: rangeStartValue,
        rangeEnd: rangeEndValue,
        data: note,
        onPress: openAddEditNoteModal,
        condensed,
      })).filter(Boolean);

      const taskEntries = (tasksResponse?.content || []).map((task) => {
        const taskDescription = (task.eventData && (task.eventData.Description?.value
          || task.eventData.Notes?.value))
          || task.description
          || '';
        return buildTimelineEntry({
          type: 'task',
          title: task.name || task.eventTemplate?.name || 'Task',
          description: taskDescription,
          date: task.startDate || task.createdDate || task.updatedDate,
          id: task.id,
          rangeStart: rangeStartValue,
          rangeEnd: rangeEndValue,
          data: task,
          onPress: (item) => selectTaskOrEvent(item, 'task'),
          condensed,
        });
      }).filter(Boolean);

      const scheduleEntries = generateEventsFromSchedules(
        schedulesResponse?.content || [],
        scheduleExceptionsResponse?.content || [],
        rangeStart.toDate(),
        rangeEnd.toDate(),
        barnUsersMap || {},
        selectedBarn?.barnMetadata || {},
      ).map((schedule) => buildTimelineEntry({
        type: 'event',
        title: schedule.title || 'Calendar Event',
        description: schedule.description || '',
        date: schedule.start,
        id: schedule.id,
        rangeStart: rangeStartValue,
        rangeEnd: rangeEndValue,
        data: schedule,
        onPress: (item) => selectTaskOrEvent(item, 'event'),
        condensed,
      })).filter(Boolean);

      const billableEntries = (billablesResponse?.content || []).map((billable) => {
        const descriptionParts = [
          billable.note || billable.taskType?.name || 'Billing entry',
          formatCurrency(billable.price),
        ].filter(Boolean);
        const status = billable.status
          ? `Status: ${billable.status}`
          : '';
        return buildTimelineEntry({
          type: 'billing',
          title: billable.note || billable.taskType?.name || 'Billing',
          description: [descriptionParts.join(' – '), status].filter(Boolean).join(' • '),
          date: billable.billableDate,
          id: billable.id,
          rangeStart: rangeStartValue,
          rangeEnd: rangeEndValue,
          data: billable,
          onPress: openAddEditBillableModal,
          condensed,
        });
      }).filter(Boolean);

      const combined = [
        ...noteEntries,
        ...taskEntries,
        ...scheduleEntries,
        ...billableEntries,
      ].sort((a, b) => b.sortDate - a.sortDate);

      setActivity(combined.filter((entry) => selectedTypes.length === 0 || selectedTypes.includes(entry.type)));
    } catch (error) {
      console.error(error);
      setActivity([]);
    } finally {
      setIsLoading(false);
    }
  }, [horseId, horseTagId, normalizedHorseId, dateRange, selectedTypes]);

  React.useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  const timelineItems = React.useMemo(() => (
    activity.map((item) => ({
      ...item,
      icon: iconMap[item.type] || <History fontSize="small" />,
    }))
  ), [activity]);

  const { mode } = useColorScheme()

  return (
    <Paper
      sx={{
        borderRadius: 3,
        p: 2,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexDirection={!condensed
          ? 'row'
          : 'column'}
        mb={2}
        gap={2}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',

            gap: 2,
          }}
        >
          <Avatar><History /></Avatar>
          <Stack spacing={0.5}>
            <Typography variant="h5" fontWeight={600}>
              Recent Activity
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Notes, tasks, events, and billings
            </Typography>
          </Stack>
        </Box>
        <Stack
          direction={{
            xs: 'column',
            md: condensed
              ? 'column'
              : 'row',
          }}
          gap={2}
          alignItems={{
            xs: 'stretch',
            md: condensed
              ? 'stretch'
              : 'center',
          }}
          flexWrap="wrap"
        >
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Stack
              direction={{
                xs: 'column',
                lg: condensed
                  ? 'column'
                  : 'row',
              }}
              gap={2}
              flexWrap="wrap"
            >
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <DatePicker
                  sx={{ width: '150px' }}
                  label="Start date"
                  value={dateRange[0]}
                  onChange={(newValue) => {
                    if (!newValue) return;
                    setQuickRange('custom');
                    setDateRange(([, end]) => {
                      const updatedEnd = end && newValue.isAfter(end)
                        ? newValue.clone().endOf('day')
                        : end;
                      return [newValue.clone().startOf('day'), updatedEnd];
                    });
                  }}
                  slotProps={{ textField: { size: 'small' } }}
                />
                <DatePicker
                  sx={{ width: '150px' }}
                  label="End date"
                  value={dateRange[1]}
                  onChange={(newValue) => {
                    if (!newValue) return;
                    setQuickRange('custom');
                    setDateRange(([start]) => {
                      const adjustedStart = start && newValue.isBefore(start)
                        ? newValue.clone().startOf('day')
                        : start;
                      return [adjustedStart, newValue.clone().endOf('day')];
                    });
                  }}
                  slotProps={{ textField: { size: 'small' } }}
                />
              </Stack>
            </Stack>
          </LocalizationProvider>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="activity-range-filter-label">Quick Range</InputLabel>
            <Select
              labelId="activity-range-filter-label"
              value={quickRange}
              label="Quick Range"
              onChange={(event) => {
                const { value } = event.target;
                setQuickRange(value);
                const shortcut = shortcutItems.find((item) => item.value === value);
                if (shortcut) {
                  setDateRange(shortcut.getValue());
                }
              }}
            >
              {shortcutItems.map((shortcut) => (
                <MenuItem key={shortcut.value} value={shortcut.value}>
                  {shortcut.label}
                </MenuItem>
              ))}
              <MenuItem value="custom">Custom</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="activity-type-filter-label">Activity Types</InputLabel>
            <Select
              labelId="activity-type-filter-label"
              multiple
              value={selectedTypes}
              onChange={(event) => {
                const { value } = event.target;
                setSelectedTypes(typeof value === 'string'
                  ? value.split(',')
                  : value);
              }}
              renderValue={(selected) => (
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {selected.map((type) => (
                    <Chip key={type} label={type.charAt(0).toUpperCase() + type.slice(1)} size="small" />
                  ))}
                </Stack>
              )}
              label="Activity Types"
            >
              {['note', 'task', 'event', 'billing'].map((type) => {
                const label = type.charAt(0).toUpperCase() + type.slice(1);
                return (
                  <MenuItem key={type} value={type}>
                    <Checkbox checked={selectedTypes.indexOf(type) > -1} />
                    {label}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Stack>
      </Stack>
      <Timeline
        position="left"
        sx={{
          p: 0,
          m: 0,
          '& .MuiTimelineItem-root:before': {
            flex: 0,
            padding: 0,
          },
          maxHeight: 600,
          overflowY: 'auto',
          '& .MuiTimelineItem-root': { mb: 2 },
          flexDirection: !condensed
            ? 'column'
            : 'row',
        }}
      >
        {
          !isLoading && timelineItems.length === 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <InfoOutlined
                sx={{
                  fontSize: '3.5rem',
                  color: grey[400],
                  m: 2,
                }}
              />
              <Typography sx={{ fontWeight: 300 }}>
                No Activity
              </Typography>
            </Box>
          )
        }
        {
          isLoading && (<LinearProgress sx={{ width: '100%' }} />)
        }
        {timelineItems.map((item, idx) => {
          const typeKey = ActivityComponentMap[item.type]
            ? item.type
            : 'activity';
          const ActivityComponent = ActivityComponentMap[typeKey] || ActivityComponentMap.activity;
          const viewHandler = ActivityViewHandlers[typeKey] || ActivityViewHandlers.activity;
          return (
            // eslint-disable-next-line react/no-array-index-key
            <TimelineItem
              // eslint-disable-next-line react/no-array-index-key
              key={`${item.type}-${item.title}-${idx}`}
              sx={{ minHeight: 'unset' }}
            >
              <TimelineContent
                sx={{
                  py: 1,
                  px: 2,

                }}
              >
                <ActivityComponent
                  item={item}
                  onView={viewHandler}
                  colorMode={mode}
                  barnUsersMap={barnUsersMap}
                  condensed={condensed}
                />
              </TimelineContent>
              <TimelineContent
                color="text.secondary"
                sx={{
                  fontSize: 14,
                  minWidth: 120,
                  flex: 0.25,
                  textAlign: {
                    xs: 'left',
                    md: condensed
                      ? 'left'
                      : 'right',
                  },
                }}
              >
                <Box>{item.timestamp}</Box>
                <Box sx={{ fontSize: 11 }}>{item.relativeTime}</Box>
              </TimelineContent>
              <TimelineSeparator>
                <TimelineDot color={getTimelineColor(item.type)}>
                  {item.icon}
                </TimelineDot>
                {idx < timelineItems.length - 1 && (
                  <TimelineConnector sx={{ backgroundColor: blueGrey[200] }} />
                )}
              </TimelineSeparator>
            </TimelineItem>
          );
        })}
      </Timeline>
    </Paper>
  );
};

HorseActivityTimeline.propTypes = {
  horseId: PropTypes.string.isRequired,
  openAddEditNoteModal: PropTypes.func.isRequired,
  openAddEditBillableModal: PropTypes.func.isRequired,
};

export default HorseActivityTimeline;
